/** The two icon folders, each a webpack context resolved by id (FT-1527). */
const fullIcon = (id) => require("../assets/icons/" + id + ".png");
const thumbIcon = (id) => require("../assets/icons-thumb/" + id + ".webp");

/**
 * Golem fork (FT-1272): A CHARACTER'S OWN ICON, from wherever it came from.
 *
 * Three surfaces had already written this same eight lines by hand —
 * NightSheet's checklist rows, ScriptView's cards, and (before FT-857 moved
 * it) EditionModal — and FT-1272 wanted a fourth: the night ask standing on
 * the clock face, whose header now leads with the role's icon. A fourth copy
 * is the point at which "everyone writes it" becomes "nobody owns it", so the
 * definition lands here, in the same `golem/` shelf every other cross-surface
 * fact lives on (belief, glyphs, nightInfo).
 *
 * THE THREE SOURCES, in the order they are asked:
 *   1. `role.golemIconData` — a forged character carries its own artwork
 *      inline (a data URI), and that always wins: it IS the character.
 *   2. the shipped PNG for this role id, when the base edition knows the id.
 *   3. `role.imageAlt` — a homebrew role naming a shipped icon to borrow.
 *   ...and `custom.png` when none of that resolves, so a caller never has to
 *   handle an empty string mid-render.
 *
 * `require` rather than `import`: the icon set is resolved by NAME at runtime
 * from a value the caller supplies, which is exactly the case a static import
 * cannot express. webpack turns the expression into its own context module
 * over the icons folder — the same mechanism the hand-written copies used.
 *
 * FT-1527: SIZE. Every shipped icon also has a small copy (256 px WebP,
 * `assets/icons-thumb/`, made by `scripts/ft1527-botc-icon-thumbs.ts` with the
 * platform's own thumbnail recipe). Pass `{ small: true }` wherever the icon is
 * drawn at roughly 64 CSS px or less — a list row, a grid card, a chip. Large
 * surfaces (the grimoire coin, the Role Studio coin) keep the original. A
 * forged character's inline art has no small copy and is returned as-is.
 *
 * @param role      the role object (needs `id`; may carry golemIconData/imageAlt)
 * @param baseById  the store's `rolesJSONbyId` getter — a Map of the base
 *                  edition's roles. Passed IN rather than reached for: this
 *                  file is a plain module and does not import the store.
 * @param opts      `{ small }` — see SIZE above.
 * @returns a URL string, always — never "" for a role that exists.
 */
export function roleIconUrl(role, baseById, opts) {
  if (!role) return "";
  if (role.golemIconData) return role.golemIconData;
  const known = baseById && baseById.has && baseById.has(role.id);
  return officialIconUrl(known ? role.id : role.imageAlt || "custom", opts);
}

/**
 * FT-1527: THE SHIPPED ICON FOR AN ID, at a size. The one place the icon
 * folders are resolved by name — `roleIconUrl` above and `roleDrag`'s own
 * lookup both come through here.
 *
 * Each `require` below is a webpack context over ONE folder (`icons/*.png`,
 * `icons-thumb/*.webp`); an id outside the folder throws, which is the miss.
 *
 * @param id    a shipped icon id (a role id, or `good`/`evil`/`custom`/…)
 * @param opts  `{ small, fallback }` — `small` asks for the thumbnail;
 *              `fallback` is a second id to try before `custom`.
 * @returns a URL string, always (the `custom` art at worst).
 */
export function officialIconUrl(id, opts) {
  const small = !!(opts && opts.small);
  const fallback = opts && opts.fallback;
  const pick = small ? thumbIcon : fullIcon;
  for (const candidate of [id, fallback]) {
    if (!candidate) continue;
    try {
      return pick(candidate);
    } catch (e) {
      // a shipped PNG with no thumb yet still resolves, full-size — the
      // `--check` test is what keeps that from shipping unnoticed
      if (small) {
        try {
          return fullIcon(candidate);
        } catch (e2) {
          // not shipped at all: next candidate
        }
      }
    }
  }
  return pick("custom");
}
